import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { TApi, TChangelogKind, TRoutes } from '../../../../shared/const'
import {
	changelogEnvLabel,
	changelogKindLabel,
	changelogRepoLabel,
	ticketTypeLabel
} from '../../../../shared/helpers'
import http from '../../../../shared/http'
import { notifyError } from '../../../../shared/errorMessage'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import { formatEffort } from '../../../../shared/effort'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'
import ListToolbar from '../../../../UI/ListToolbar'
import Search from '../../../../UI/Search'
import TabBar from '../../../../UI/TabBar'
import BtnFilter from '../../../../UI/BtnFilter'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import {
	changelogAreaField,
	changelogCategoryField,
	changelogEnvField,
	changelogKindField,
	changelogProjectField,
	changelogRepoField,
	changelogTicketField,
	changelogTypeField
} from '../../../../UI/FilterPanel/fields'

import ChangelogStats from './ChangelogStats'
import EntryEditModal from './EntryEditModal'
import styles from './style.module.scss'
import { formatDate as formatDateInVienna } from '../../../../shared/datetime'

const SORT_OPTIONS = [
	{ value: 'occurredAt:desc', label: 'Datum, neueste zuerst' },
	{ value: 'occurredAt:asc', label: 'Datum, älteste zuerst' },
	{ value: 'project:asc', label: 'Projekt' },
	{ value: 'area:asc', label: 'Bereich' },
	{ value: 'category:asc', label: 'Einordnung' },
	{ value: 'title:asc', label: 'Titel A–Z' }
]

const formatDate = value => formatDateInVienna(value, { dateStyle: 'medium' })

// Die Auswertung ist ein Reiter und keine eigene Seite: Sie rechnet mit
// denselben Filtern, und die sollen beim Wechseln stehen bleiben.
const TABS = [
	{ key: 'entries', label: 'Einträge' },
	{ key: 'stats', label: 'Auswertung' }
]

/**
 * Entwicklungen und Auslieferungen.
 *
 * Zweck ist die Beilage zur Abrechnung: sichtbar machen, wie viele kleine
 * Änderungen laufend entstehen. Deshalb ist die Voreinstellung die vollständige
 * Liste und nicht eine gefilterte Auswahl.
 */
export default function Changelog() {
	const [rows, setRows] = useState(null)
	const [count, setCount] = useState(null)
	const [facets, setFacets] = useState(null)

	const [search, setSearch] = useState('')
	const [showFilter, setShowFilter] = useState(false)
	const [sort, setSort] = useState('occurredAt:desc')
	// Kacheln als Voreinstellung: Sie zeigen Beschreibung und Projekt einer
	// Änderung auf einen Blick, die Liste ist eher zum Durchgehen.
	const [view, setView] = useState('tiles')
	const [expanded, setExpanded] = useState({})
	const [editing, setEditing] = useState(null)
	const [tab, setTab] = useState('entries')

	const loadFacets = useCallback(() => {
		http.get(TApi.ADMIN_CHANGELOG_FACETS)
			.then(({ data }) => setFacets(data))
			.catch(() => setFacets({ categories: [], areas: [], projects: [] }))
	}, [])

	const projects = useMemo(() => facets?.projects || [], [facets])

	// Dieselben Felder wie in den uebrigen Listen, nur aus den Kennzahlen dieser
	// Ansicht gespeist. Sie stehen in FilterPanel/fields, nicht hier.
	const fields = useMemo(
		() => [
			changelogProjectField(projects),
			changelogTypeField(),
			changelogTicketField(),
			changelogKindField(),
			changelogRepoField(),
			changelogEnvField(),
			changelogCategoryField(facets?.categories),
			changelogAreaField(facets?.areas)
		],
		[facets, projects]
	)

	const filters = useFilters({ storageKey: 'changelog', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)

	const load = useCallback(() => {
		const [order, direction] = sort.split(':')

		// `disableToast`: Der eigene Satz benennt, was nicht geladen werden konnte.
		http.get(TApi.ADMIN_CHANGELOG, {
			params: { search: debouncedSearch, filter: filterQuery, order, direction, limit: 500 },
			disableToast: true
		})
			.then(({ data }) => {
				setRows(data.rows)
				setCount(data.count)
			})
			.catch(error => {
				notifyError(error, 'Die Liste konnte nicht geladen werden.')
				setRows([])
				setCount(0)
			})
	}, [debouncedSearch, filterQuery, sort])

	useEffect(() => {
		loadFacets()
	}, [loadFacets])

	useEffect(() => {
		load()
	}, [load])

	const reset = () => {
		setSearch('')
		filters.resetAll()
	}

	/**
	 * Projekt einer Entwicklung zuordnen — direkt an der Zeile bzw. Kachel.
	 *
	 * Zuordnen muss schnell gehen: Es sind viele Einträge, und die Zuordnung
	 * ist die einzige Angabe, die nicht schon aus der Versionsverwaltung kommt.
	 * Der Weg über „Bearbeiten" bliebe für den Rest.
	 *
	 * Die geänderte Zeile bleibt danach stehen, statt die Liste neu zu laden:
	 * Sonst springt beim Zuordnen unter einem Projektfilter der eben bearbeitete
	 * Eintrag weg und man verliert die Stelle, an der man war.
	 */
	const assignProject = (entry, value) => {
		const projectId = value ? Number(value) : null

		// `disableToast`: Der eigene Satz benennt den Vorgang, der schiefging.
		http.put(TApi.ADMIN_CHANGELOG_ID(entry.id), { projectId }, { disableToast: true })
			.then(({ data }) =>
				setRows(prev =>
					prev.map(row =>
						row.id === entry.id
							? { ...row, projectId: data.projectId, projectTitle: data.projectTitle }
							: row
					)
				)
			)
			.catch(error => notifyError(error, 'Die Zuordnung konnte nicht gespeichert werden.'))
	}

	const projectOptions = projects.map(project => ({
		value: String(project.id),
		label: project.title
	}))

	const projectPicker = entry => (
		<select
			className={`${styles.projectPicker} ${entry.projectId ? '' : styles.projectPickerEmpty}`}
			value={entry.projectId ? String(entry.projectId) : ''}
			title="Projekt zuordnen"
			aria-label={`Projekt für „${entry.title}"`}
			onChange={event => assignProject(entry, event.target.value)}
		>
			<option value="">Ohne Projekt</option>
			{projectOptions.map(option => (
				<option key={option.value} value={option.value}>
					{option.label}
				</option>
			))}
		</select>
	)

	// Der Aufwand gehört nur zu Entwicklungen. Fehlt er, steht das ausdrücklich
	// da — ein leerer Platz sähe aus wie „kein Aufwand".
	const effortTag = entry => (
		<span
			className={`${styles.tagEffort} ${entry.effortMinutes == null ? styles.tagEffortEmpty : ''}`}
			title="Geschätzter Aufwand"
		>
			{entry.effortMinutes == null ? 'Aufwand offen' : formatEffort(entry.effortMinutes)}
		</span>
	)

	/*
	 * Art und Anforderung einer Entwicklung. Die Nummer führt in die
	 * Anforderung (dieselbe Adresse, mit der die Erinnerungsmails sie öffnen);
	 * dort führt „Entwicklungen" wieder hierher zurück.
	 */
	const typeTag = entry =>
		entry.type ? (
			<span className={`${styles.tagType} ${styles[`tagType${entry.type}`] || ''}`} title="Art">
				{ticketTypeLabel(entry.type)}
			</span>
		) : null

	const ticketLink = entry =>
		entry.ticket ? (
			<Link
				className={styles.ticketLink}
				to={`${TRoutes.TICKETS}?nr=${entry.ticket.id}`}
				title={`Anforderung #${entry.ticket.id} öffnen`}
			>
				#{entry.ticket.id} {entry.ticket.title}
			</Link>
		) : null

	/**
	 * Ausgabe als CSV — das ist die eigentliche Beilage.
	 *
	 * Exportiert wird, was gerade zu sehen ist. Wer nach einem Zeitraum oder
	 * Bereich filtert, bekommt genau diese Auswahl; alles andere wäre beim
	 * Zusammenstellen einer Abrechnung eine Falle.
	 */
	const exportCsv = () => {
		if (!rows?.length) return

		const escape = value => `"${String(value ?? '').replace(/"/g, '""')}"`
		const header = [
			'Datum',
			'Art',
			'Umgebung',
			'Teil',
			'Projekt',
			'Art',
			'Anforderung',
			'Einordnung',
			'Bereich',
			'Titel',
			'Beschreibung',
			'Aufwand',
			// Die Minuten zusätzlich als Zahl: Damit lässt sich in Excel summieren.
			'Aufwand (Minuten)'
		]
		const lines = rows.map(row =>
			[
				formatDate(row.occurredAt),
				changelogKindLabel(row.kind),
				row.environment ? changelogEnvLabel(row.environment) : '',
				changelogRepoLabel(row.repo),
				row.projectTitle || '',
				row.type ? ticketTypeLabel(row.type) : '',
				row.ticket ? `#${row.ticket.id} ${row.ticket.title}` : '',
				row.category || '',
				row.area || '',
				row.title,
				row.summary || '',
				row.effortMinutes == null ? '' : formatEffort(row.effortMinutes),
				row.effortMinutes ?? ''
			]
				.map(escape)
				.join(';')
		)

		// BOM voran, sonst zeigt Excel die Umlaute falsch an.
		const csv = `﻿${[header.map(escape).join(';'), ...lines].join('\r\n')}`
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		const url = URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.download = `Entwicklungen-${new Date().toISOString().slice(0, 10)}.csv`
		document.body.appendChild(link)
		link.click()
		link.remove()
		URL.revokeObjectURL(url)
	}

	if (rows === null) return <Loading />

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title="Entwicklungen und Auslieferungen" bg="#f8faff">
				<div className={styles.headerContent}>
					{/*
					 * Reihenfolge wie in jeder anderen Liste: ganz rechts der Filter,
					 * links davor die Suche, Handlungen der Seite davor.
					 */}
					<Btn theme="secondary" size="small" disabled={!rows.length} onClick={exportCsv}>
						Als CSV ausgeben
					</Btn>
					<Search
						value={search}
						onChange={e => setSearch(e.target.value)}
						placeholder="Titel, Beschreibung, Bereich oder #Anforderung"
						onSubmit={e => e.preventDefault()}
					/>
					<BtnFilter
						icon="arrow"
						onClick={() => setShowFilter(true)}
						count={filters.count}
					>
						Filter ({filters.count})
					</BtnFilter>
				</div>
			</MiniHeader>

			<TabBar className={styles.tabs} tabs={TABS} active={tab} onChange={setTab} />

			{tab === 'stats' ? (
				<ChangelogStats search={debouncedSearch} filterQuery={filterQuery} onFilter={filters.setField} />
			) : (
				<>
				{/*
				 * In der Leiste bleiben nur Sortierung, Ansichtswechsel und die Zahl der
				 * Treffer. Gesucht und gefiltert wird ueber die Kopfleiste und die
				 * Leiste rechts — wie in allen anderen Listen.
				 */}
				<ListToolbar
					className={styles.toolbar}
					search={search}
					showSearch={false}
					onSearchChange={setSearch}
					onFilterChange={() => {}}
					extraActive={filters.count}
					sort={sort}
					sortOptions={SORT_OPTIONS}
					onSortChange={setSort}
					view={view}
					onViewChange={setView}
					onReset={reset}
					count={count}
					countLabel={count === 1 ? 'Eintrag' : 'Einträge'}
				/>

				{!rows.length ? (
					<div className={styles.empty}>Nichts gefunden. Filter oder Suche zurücksetzen?</div>
				) : view === 'list' ? (
					<div className={styles.list}>
						{rows.map(entry => {
							const isDeployment = entry.kind === TChangelogKind.DEPLOYMENT

							return (
								<article
									key={entry.id}
									className={`${styles.item} ${isDeployment ? styles.itemDeploy : ''}`}
								>
									<div className={styles.itemDate}>{formatDate(entry.occurredAt)}</div>

									<div className={styles.itemMain}>
										<h3 className={styles.itemTitle}>{entry.title}</h3>
										{entry.summary && <p className={styles.itemText}>{entry.summary}</p>}
										{ticketLink(entry)}
									</div>

									<div className={styles.itemTags}>
										{projectPicker(entry)}
										{isDeployment ? (
											<span className={`${styles.tag} ${styles.tagDeploy}`}>
												Auslieferung {changelogEnvLabel(entry.environment)}
											</span>
										) : (
											<>
												{typeTag(entry)}
												{entry.category && (
													<span className={styles.tag}>{entry.category}</span>
												)}
												{entry.area && (
													<span className={styles.tagArea}>{entry.area}</span>
												)}
												{!entry.userFacing && (
													<span className={styles.tagInternal} title="Für Anwender:innen nicht sichtbar">
														technisch
													</span>
												)}
												{effortTag(entry)}
											</>
										)}
										<button
											type="button"
											className={styles.editLink}
											onClick={() => setEditing(entry)}
										>
											Bearbeiten
										</button>
									</div>
								</article>
							)
						})}
					</div>
				) : (
					<div className={styles.tiles}>
						{rows.map(entry => {
							const isOpen = expanded[entry.id]
							const text = entry.summary || ''
							const isLong = text.length > 150

							return (
								<article
									key={entry.id}
									className={`${styles.tile} ${
										entry.kind === TChangelogKind.DEPLOYMENT ? styles.tileDeploy : ''
									}`}
								>
									<header className={styles.tileHead}>
										<span className={styles.tileDate}>
											{formatDate(entry.occurredAt)}
											{entry.kind !== TChangelogKind.DEPLOYMENT && effortTag(entry)}
										</span>
										{/* Auslieferungen sind auch in den Kacheln als solche
										    benannt und nicht nur farblich abgesetzt — die Kacheln
										    sind die zuerst gezeigte Ansicht. */}
										{entry.kind === TChangelogKind.DEPLOYMENT ? (
											<span className={`${styles.tag} ${styles.tagDeploy}`}>
												Auslieferung {changelogEnvLabel(entry.environment)}
											</span>
										) : (
											<span className={styles.tileHeadTags}>
												{typeTag(entry)}
												{entry.category && <span className={styles.tag}>{entry.category}</span>}
											</span>
										)}
									</header>

									<h3 className={styles.tileTitle}>{entry.title}</h3>

									{/* Das Projekt steht oben und nicht bei den Kennzeichnungen im
									    Fuß: Es beantwortet die Frage „wozu gehört das?", und die
									    stellt sich beim Lesen zuerst. */}
									<div className={styles.tileProject}>{projectPicker(entry)}</div>
									{ticketLink(entry)}

									{text && (
										<p className={styles.tileText}>
											{isOpen || !isLong ? text : `${text.slice(0, 150)}…`}
										</p>
									)}

									<footer className={styles.tileFoot}>
										{isLong ? (
											<button
												type="button"
												className={styles.moreLink}
												onClick={() =>
													setExpanded(prev => ({ ...prev, [entry.id]: !isOpen }))
												}
											>
												{isOpen ? 'Weniger' : 'Mehr lesen'}
											</button>
										) : (
											<span />
										)}
										<span className={styles.tileFootTags}>
											{!entry.userFacing && (
												<span
													className={styles.tagInternal}
													title="Für Anwender:innen nicht sichtbar"
												>
													technisch
												</span>
											)}
											{entry.area && <span className={styles.tagArea}>{entry.area}</span>}
										</span>
									</footer>
								</article>
							)
						})}
					</div>
				)}
				</>
			)}

			{editing && (
				<EntryEditModal
					entry={editing}
					projects={projects}
					onClose={() => setEditing(null)}
					onSaved={() => {
						setEditing(null)
						load()
						loadFacets()
					}}
				/>
			)}

			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={filters.setField}
				onResetAll={filters.resetAll}
			/>
		</div>
	)
}
